import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  Image,
  Card,
  Typography,
  Tooltip,
  Empty,
  Modal,
} from 'antd';
import {
  PlayCircleOutlined,
  AudioOutlined,
  DeleteOutlined,
  UploadOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { MediaFile } from '../../services/media';
import mediaService from '../../services/media';
import MediaLibraryModal from './MediaLibraryModal';
import MediaUploader from './MediaUploader';
import { getFileIconByType } from '../../config/file-type-icons';

const { Text } = Typography;

interface MediaSelectorProps {
  value?: number | number[]; // 媒体ID或ID数组
  onChange?: (value: number | number[] | undefined) => void;
  multiple?: boolean;
  maxCount?: number;
  fileTypes?: ('image' | 'document' | 'video' | 'audio' | 'other')[];
  accept?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'default' | 'large';
  showPreview?: boolean;
  style?: React.CSSProperties;
  className?: string;
  alwaysShowActions?: boolean;
}

interface MediaDisplayProps {
  media: MediaFile;
  size: 'small' | 'default' | 'large';
  showPreview: boolean;
  onRemove?: () => void;
  onDownload?: () => void;
  onClick?: () => void;
}

const MediaDisplay: React.FC<MediaDisplayProps> = ({
  media,
  size,
  showPreview,
  onRemove,
  onDownload,
  onClick,
}) => {
  const mediaUrl = mediaService.getMediaUrl(media);
  const sizeMap = {
    small: 80,
    default: 120,
    large: 160,
  };
  const displaySize = sizeMap[size];

  const renderMediaContent = () => {
    switch (media.file_type) {
      case 'image':
        return showPreview ? (
          <Image
            src={mediaUrl}
            alt={media.alt_text || media.title}
            style={{
              width: displaySize,
              height: displaySize * 0.75,
              objectFit: 'cover',
              borderRadius: 4,
            }}
            preview={{
              mask: false,
            }}
          />
        ) : (
          <div
            style={{
              width: displaySize,
              height: displaySize * 0.75,
              background: `url(${mediaUrl}) center/cover`,
              borderRadius: 4,
              cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
          />
        );

      case 'video':
        return (
          <div
            style={{
              width: displaySize,
              height: displaySize * 0.75,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
          >
            <PlayCircleOutlined style={{ fontSize: displaySize * 0.3, color: '#1890ff' }} />
          </div>
        );

      case 'audio':
        return (
          <div
            style={{
              width: displaySize,
              height: displaySize * 0.75,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
          >
            <AudioOutlined style={{ fontSize: displaySize * 0.3, color: '#52c41a' }} />
          </div>
        );

      default:
        return (
          <div
            style={{
              width: displaySize,
              height: displaySize * 0.75,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              borderRadius: 4,
              cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
          >
            <div style={{ marginBottom: 4 }}>
              {getFileIconByType(mediaService.getFileTypeIcon(media))}
            </div>
            <Text style={{ fontSize: size === 'small' ? 10 : 12 }}>
              {media.file_type || '文件'}
            </Text>
          </div>
        );
    }
  };

  return (
    <Card
      hoverable={!!onClick}
      style={{ width: displaySize + 16 }}
      styles={{ body: { padding: 8 } }}
      actions={
        onRemove ? [
          <Tooltip title="移除">
            <DeleteOutlined key="delete" onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }} />
          </Tooltip>
        ] : []
      }
    >
      {renderMediaContent()}

      <div style={{ marginTop: 8 }}>
        <Tooltip title={`点击下载: ${media.title}`}>
          <Text
            ellipsis
            style={{
              fontSize: size === 'small' ? 11 : 12,
              display: 'block',
              cursor: onDownload ? 'pointer' : 'default',
              color: onDownload ? '#1890ff' : 'inherit',
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDownload?.();
            }}
          >
            {media.title}
          </Text>
        </Tooltip>

      </div>
    </Card>
  );
};

const MediaSelector: React.FC<MediaSelectorProps> = ({
  value,
  onChange,
  multiple = false,
  maxCount,
  fileTypes,
  accept,
  placeholder = '选择媒体文件',
  disabled = false,
  size = 'default',
  showPreview = true,
  style,
  className,
  alwaysShowActions = false,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [initialFilter, setInitialFilter] = useState<string | undefined>(undefined);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);

  // 根据value加载媒体文件信息
  useEffect(() => {
    loadMediaFiles();
  }, [value]);

  const loadMediaFiles = async () => {
    if (!value) {
      setSelectedMedia([]);
      return;
    }

    setLoading(true);
    try {
      const ids = Array.isArray(value) ? value : [value];
      const mediaPromises = ids.map(id => mediaService.getMedia(id));
      const responses = await Promise.all(mediaPromises);

      const mediaFiles = responses
        .filter(response => response.success && response.data)
        .map(response => response.data!);

      setSelectedMedia(mediaFiles);
    } catch (error) {
      console.error('Failed to load media files:', error);
      setSelectedMedia([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (selected: MediaFile | MediaFile[]) => {
    const newSelection = Array.isArray(selected) ? selected : [selected];

    if (multiple) {
      // 多选模式：合并选择
      const existingIds = selectedMedia.map(m => m.id);
      const uniqueNewSelection = newSelection.filter(m => !existingIds.includes(m.id));
      const combined = [...selectedMedia, ...uniqueNewSelection];

      // 检查最大数量限制
      if (maxCount && combined.length > maxCount) {
        const limited = combined.slice(0, maxCount);
        setSelectedMedia(limited);
        onChange && onChange(limited.map(m => m.id));
      } else {
        setSelectedMedia(combined);
        onChange && onChange(combined.map(m => m.id));
      }
    } else {
      // 单选模式
      setSelectedMedia(newSelection);
      onChange && onChange(newSelection[0]?.id);
    }

    setModalVisible(false);
  };

  const handleRemove = (mediaToRemove: MediaFile) => {
    const newSelection = selectedMedia.filter(m => m.id !== mediaToRemove.id);
    setSelectedMedia(newSelection);

    if (multiple) {
      const newIds = newSelection.map(m => m.id);
      onChange && onChange(newIds.length > 0 ? newIds : undefined);
    } else {
      onChange && onChange(undefined);
    }
  };

  const handleRemoveAll = () => {
    setSelectedMedia([]);
    onChange && onChange(undefined);
  };



  const handleUploadSuccess = (mediaFile: MediaFile) => {
    if (multiple) {
      // 多选模式：添加到现有选择
      setSelectedMedia(prev => {
        const newSelection = [...prev, mediaFile];

        // 检查最大数量限制
        if (maxCount && newSelection.length > maxCount) {
          const limited = newSelection.slice(0, maxCount);
          onChange && onChange(limited.map(m => m.id));
          return limited;
        } else {
          onChange && onChange(newSelection.map(m => m.id));
          return newSelection;
        }
      });
    } else {
      // 单选模式：替换现有选择
      setSelectedMedia([mediaFile]);
      onChange && onChange(mediaFile.id);
    }

    // 上传成功后自动关闭上传模态框
    setUploadModalVisible(false);
  };

  const canAddMore = () => {
    if (!multiple) return selectedMedia.length === 0;
    return !maxCount || selectedMedia.length < maxCount;
  };

  const getSelectionMode = (): 'single' | 'multiple' => {
    if (!multiple) return 'single';
    if (maxCount === 1) return 'single';
    return 'multiple';
  };

  const getMaxSelection = (): number | undefined => {
    if (!multiple) return 1;
    if (maxCount) {
      return Math.max(0, maxCount - selectedMedia.length);
    }
    return undefined;
  };

  if (loading) {
    return (
      <div style={{ ...style, minHeight: 100 }} className={className}>
        <Card loading />
      </div>
    );
  }

  return (
    <div style={style} className={className}>
      {/* 已选择的媒体文件显示 */}
      {selectedMedia.length > 0 && showPreview && (
        <div style={{ marginBottom: 16 }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 8
          }}>
            {selectedMedia.map((media) => (
              <MediaDisplay
                key={media.id}
                media={media}
                size={size}
                showPreview={showPreview}
                onDownload={() => mediaService.downloadMedia(media.id, media.original_name)}
                onRemove={() => handleRemove(media)}
              />
            ))}
          </div>

          {multiple && selectedMedia.length > 1 && (
            <Button
              size="small"
              danger
              onClick={handleRemoveAll}
              style={{ marginBottom: 8 }}
            >
              清空所有
            </Button>
          )}
        </div>
      )}

      {/* 操作按钮 */}
      {(alwaysShowActions || selectedMedia.length === 0 || canAddMore()) && (
        <div style={{
          borderRadius: 6,
          padding: 0,
          textAlign: 'left',
          minHeight: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}>
          <Space direction="horizontal" size={12}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              disabled={disabled}
              onClick={() => setUploadModalVisible(true)}
            >
              上传新文件
            </Button>
            <Button
              type="default"
              icon={<FolderOpenOutlined />}
              disabled={disabled}
              onClick={() => {
                if (fileTypes && fileTypes.length === 1) {
                  setInitialFilter(fileTypes[0]);
                } else {
                  setInitialFilter(undefined);
                }
                setModalVisible(true);
              }}
            >
              选择现有文件
            </Button>
          </Space>
        </div>
      )}

      {/* 空状态 */}
      {selectedMedia.length === 0 && !canAddMore() && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="无可选择的文件"
          style={{ padding: 24 }}
        />
      )}

      {/* 媒体库选择模态框 */}
      <MediaLibraryModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSelect={handleSelect}
        selectionMode={getSelectionMode()}
        fileTypes={initialFilter ? [initialFilter] : fileTypes}
        maxSelection={getMaxSelection()}
        title={multiple ? '选择媒体文件' : '选择媒体文件'}
      />

      {/* 上传文件模态框 */}
      <Modal
        title="上传新文件"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={600}
      >
        <MediaUploader
          onUploadSuccess={handleUploadSuccess}
          onUploadError={(error) => {
            console.error('Upload error:', error);
          }}
          multiple={true} // 支持选择多个文件同时上传
          maxSize={50}
          accept={fileTypes ? fileTypes.map(type => {
            switch(type) {
              case 'image': return 'image/*';
              case 'document': return '.pdf,.doc,.docx,.txt';
              case 'video': return 'video/*';
              case 'audio': return 'audio/*';
              default: return '*';
            }
          }).join(',') : undefined}
        />
      </Modal>
    </div>
  );
};

export default MediaSelector;
